<template>
  <aside v-if="operations.notices.length" class="payment-operations" :aria-label="t('operations.title')">
    <div class="payment-operations__header">
      <strong>{{ t('operations.title') }} · {{ operations.notices.length }}</strong>
      <BaseButton
        monochrome
        variant="ghost"
        size="sm"
        :aria-expanded="!collapsed"
        @click="collapsed = !collapsed"
      >
        {{ t(collapsed ? 'operations.expand' : 'operations.collapse') }}
      </BaseButton>
    </div>
    <div v-if="!collapsed" class="payment-operations__details" aria-live="polite">
      <article
        v-for="notice in operations.notices"
        :key="notice.key"
        class="payment-operations__item"
        :data-operation="notice.key"
      >
        <p v-if="!operationError(notice.key)" data-financial-status>{{ t(message(notice.item.status, notice.complete)) }}</p>
        <ul v-if="!operationError(notice.key) && notice.item.flows?.length" class="payment-operations__flows">
          <li v-for="flow in notice.item.flows" :key="flow.type">
            {{ t('recovery.flow', { type: flow.type }) }} — {{ t(`recovery.flowStatus.${flow.status}`) }}
            <span v-if="flow.purchase_status === 'purchased'"> · {{ t('recovery.purchased') }}</span>
          </li>
        </ul>
        <p v-if="operationError(notice.key)" data-operation-error>{{ t('operations.error') }}</p>
        <p v-if="operations.dismissErrors[notice.key]" role="alert">{{ t('operations.hideError') }}</p>
        <BaseButton
          v-if="notice.dismissible"
          monochrome
          variant="ghost"
          size="sm"
          :loading="operations.dismissing[notice.key]"
          @click="operations.dismiss(notice.key)"
        >
          {{ t(notice.key.startsWith('utp:') ? 'operations.hide' : 'operations.close') }}
        </BaseButton>
      </article>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { inject, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { routeLocationKey } from 'vue-router'
import BaseButton from '@/shared/components/base/BaseButton.vue'
import { useProfileStore } from '@/modules/profile/store/profileStore'
import { getApiBaseUrl } from '@/shared/api/httpClient'
import { usePaymentOperationsStore } from '../store/paymentOperationsStore'
import { paymentOperationMessages } from '../locales/paymentOperations'
import { portalRecoveryMessages } from '../locales/portalRecovery'

const { t } = useI18n({ useScope: 'local', messages: {
  en: { ...paymentOperationMessages.en, ...portalRecoveryMessages.en },
  ru: { ...paymentOperationMessages.ru, ...portalRecoveryMessages.ru },
} })
const operations = usePaymentOperationsStore()
const profile = useProfileStore()
const route = inject(routeLocationKey, null)
const collapsed = ref(false)
if (route) {
  watch(() => /^\/wallet(?:\/|$)/.test(route.path), active => operations.setWallet(active), { immediate: true, flush: 'sync' })
}
watch(() => [profile.profile?.id ?? null, `${getApiBaseUrl()}:${import.meta.env.VITE_X_SPACE_ID ?? ''}`] as const,
  ([owner, site]) => operations.setSession(owner, site), { immediate: true, flush: 'sync' })
onMounted(operations.start)
onUnmounted(() => {
  if (route) operations.setWallet(null)
  operations.stop()
})
function operationError(key: string) {
  return operations.errors[key] || (key.startsWith('portal:') && operations.portal.statusErrors[key.slice(7)])
}
function message(status: string, complete: boolean) {
  if (complete) return status === 'prepaid' ? 'operations.prepaid' : 'operations.activated'
  if (['delayed', 'manual_review'].includes(status)) return 'operations.waiting'
  if (['exchanged', 'partially_purchased', 'credited', 'activating'].includes(status)) return 'operations.purchase'
  return 'operations.received'
}
</script>

<style scoped lang="scss">
.payment-operations {
  position: fixed;
  right: 20px;
  bottom: 90px;
  z-index: 1100;
  width: min(390px, calc(100vw - 24px));
  padding: 14px;
  box-sizing: border-box;
  border: 1px solid var(--ds-border);
  border-radius: var(--ds-radius-card);
  background: var(--ds-surface);
  color: var(--ds-text);
  font-size: var(--ds-text-size-body);
  &__header { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
  &__details { max-height: 45vh; overflow-y: auto; overflow-wrap: anywhere; }
  &__item { padding-block: 8px; border-top: 1px solid var(--ds-border); }
  &__flows { padding-inline-start: 20px; }
  @media (max-width: 768px) { top: calc(72px + env(safe-area-inset-top)); bottom: auto; right: 12px; }
}
</style>
